<script lang="ts">
export default {
  name: 'NotificationPreferences',
};
</script>

<script setup lang="ts">
import { computed, onBeforeMount, reactive, ref } from 'vue';
import notificationService from '@/services/v2/NotificationService';
import { useUser } from '@/composables/useUser';
import { UserRole } from '@/types/UserRole';

// -----
// refs
// -----

const loading = ref(false);
const { role } = useUser();

const isClientUser = computed(() => {
  return role.value === UserRole.Client;
});

const generalPrefs = reactive({
  notificationsEnabled: true,
  sendEmail: true,
});

const renewalPrefs = reactive({
  enabled: true,
  sendEmail: true,
  servicingRole: true,
  favorited: true,
  daysBefore30: false,
  daysBefore60: false,
  daysBefore90: false,
  daysBefore120: true,
});

const newPolicyPrefs = reactive({
  enabled: true,
  sendEmail: true,
  servicingRole: true,
  favorited: true,
});

const newDocumentUploadPrefs = reactive({
  enabled: false,
  sendEmail: true,
  servicingRole: true,
  favorited: true,
});

const newClientDocumentUploadPrefs = reactive({
  enabled: false,
  sendEmail: true,
  servicingRole: true,
  favorited: true,
});

const newAnalyticsResourcePrefs = reactive({
  enabled: true,
  sendEmail: true,
});

// ----------------
// lifecycle hooks
// ----------------


onBeforeMount(async () => {
  loading.value = true;
  const prefs = await notificationService.getPreferences()

  if (!prefs) {
    loading.value = false;
    return;
  }

  generalPrefs.notificationsEnabled = prefs.enabled;
  generalPrefs.sendEmail = prefs.sendEmail;

  renewalPrefs.enabled = prefs.renewalEnabled
  renewalPrefs.sendEmail = prefs.renewalSendEmail
  renewalPrefs.servicingRole = prefs.renewalServicingRole
  renewalPrefs.favorited = prefs.renewalFavorited
  renewalPrefs.daysBefore30 = prefs.renewal30Days
  renewalPrefs.daysBefore60 = prefs.renewal60Days
  renewalPrefs.daysBefore90 = prefs.renewal90Days
  renewalPrefs.daysBefore120 = prefs.renewal120Days

  newPolicyPrefs.enabled = prefs.newPolicyEnabled
  newPolicyPrefs.sendEmail = prefs.newPolicySendEmail
  newPolicyPrefs.servicingRole = prefs.newPolicyServicingRole
  newPolicyPrefs.favorited = prefs.newPolicyFavorited

  newDocumentUploadPrefs.enabled = prefs.newDocumentUploadEnabled
  newDocumentUploadPrefs.sendEmail = prefs.newDocumentUploadSendEmail
  newDocumentUploadPrefs.servicingRole = prefs.newDocumentUploadServicingRole
  newDocumentUploadPrefs.favorited = prefs.newDocumentUploadFavorited

  newClientDocumentUploadPrefs.enabled = prefs.newClientDocumentUploadEnabled
  newClientDocumentUploadPrefs.sendEmail = prefs.newClientDocumentUploadSendEmail
  newClientDocumentUploadPrefs.servicingRole = prefs.newClientDocumentUploadServicingRole
  newClientDocumentUploadPrefs.favorited = prefs.newClientDocumentUploadFavorited

  newAnalyticsResourcePrefs.enabled = prefs.newAnalyticsResourcePublishedEnabled
  newAnalyticsResourcePrefs.sendEmail = prefs.newAnalyticsResourcePublishedSendEmail

  loading.value = false;
});

// ---------------------
// handlers / functions
// ---------------------

async function handlePreferenceUpdate(): Promise<void> {
  await notificationService.updatePreferences({
    enabled: generalPrefs.notificationsEnabled,
    sendEmail: generalPrefs.sendEmail,
    renewalEnabled: renewalPrefs.enabled,
    renewalSendEmail: renewalPrefs.sendEmail,
    renewalServicingRole: renewalPrefs.servicingRole,
    renewalFavorited: renewalPrefs.favorited,
    renewal30Days: renewalPrefs.daysBefore30,
    renewal60Days: renewalPrefs.daysBefore60,
    renewal90Days: renewalPrefs.daysBefore90,
    renewal120Days: renewalPrefs.daysBefore120,
    newPolicyEnabled: newPolicyPrefs.enabled,
    newPolicySendEmail: newPolicyPrefs.sendEmail,
    newPolicyServicingRole: newPolicyPrefs.servicingRole,
    newPolicyFavorited: newPolicyPrefs.favorited,
    newDocumentUploadEnabled: newDocumentUploadPrefs.enabled,
    newDocumentUploadSendEmail: newDocumentUploadPrefs.sendEmail,
    newDocumentUploadServicingRole: newDocumentUploadPrefs.servicingRole,
    newDocumentUploadFavorited: newDocumentUploadPrefs.favorited,
    newClientDocumentUploadEnabled: newClientDocumentUploadPrefs.enabled,
    newClientDocumentUploadSendEmail: newClientDocumentUploadPrefs.sendEmail,
    newClientDocumentUploadServicingRole: newClientDocumentUploadPrefs.servicingRole,
    newClientDocumentUploadFavorited: newClientDocumentUploadPrefs.favorited,
    newAnalyticsResourcePublishedEnabled: newAnalyticsResourcePrefs.enabled,
    newAnalyticsResourcePublishedSendEmail: newAnalyticsResourcePrefs.sendEmail,
  })
}

</script>

<template>
  <div class="tw:flex tw:flex-col tw:items-center tw:w-full tw:pt-5">

    <h1>Manage Your Notifications</h1>

    <main v-show="!loading" class="tw:px-16 tw:mt-5 tw:pb-10 tw:max-w-2xl tw:container">
      <div class="tw:flex tw:flex-col tw:gap-5">
        <!-- General Settings -->
        <section class="tw:p-5 tw:flex tw:flex-col tw:rounded-xl tw:border tw:border-solid tw:border-gray-200 tw:drop-shadow-xs">
          <h2 class="tw:text-lg tw:font-bold">General Settings</h2>

          <article>
            <div class="tw:mt-2 tw:flex tw:flex-col tw:w-full tw:gap-0.5">

              <label for="notificationsEnabled" class="tw:label tw:flex tw:items-center">
                <span class="tw:pl-2 tw:select-none tw:cursor-pointer">Notifications enabled</span>
                <input type="checkbox" id="notificationsEnabled" @change="handlePreferenceUpdate"
                       v-model="generalPrefs.notificationsEnabled" :checked="generalPrefs.notificationsEnabled"
                       class="tw:toggle tw:toggle-md tw:toggle-success tw:border tw:border-solid tw:border-gray-400"
                />
              </label>

              <label for="sendEmail" class="tw:label tw:flex tw:items-center">
                <span class="tw:pl-2 tw:select-none tw:cursor-pointer">Email notification summary enabled</span>
                <input type="checkbox" id="sendEmail" :disabled="!generalPrefs.notificationsEnabled" @change="handlePreferenceUpdate"
                       v-model="generalPrefs.sendEmail" :checked="generalPrefs.sendEmail"
                       class="tw:toggle tw:toggle-md tw:toggle-success tw:border tw:border-solid tw:border-gray-400"
                />
              </label>
            </div>
          </article>
        </section>
        <!-- Renewal Notifications -->
        <section v-if="!isClientUser" class="tw:p-5 tw:flex tw:flex-col tw:rounded-xl tw:border tw:border-solid tw:border-gray-200 tw:drop-shadow-xs">
          <h2 class="tw:text-lg tw:font-bold">Renewal Notification</h2>

          <article>
            <div class="tw:mt-2 tw:flex tw:flex-col tw:w-full tw:gap-0.5">

              <label for="renewalEnabled" class="tw:label tw:pl-4 tw:flex tw:items-center tw:m-0">

                <span class="tw:select-none tw:cursor-pointer">Enabled</span>

                <input type="checkbox" id="renewalEnabled" :disabled="!generalPrefs.notificationsEnabled" @change="handlePreferenceUpdate"
                       v-model="renewalPrefs.enabled" :checked="renewalPrefs.enabled"
                       class="tw:toggle tw:toggle-md tw:toggle-success tw:border tw:border-solid tw:border-gray-400"
                />
              </label>

              <label for="renewalEmail" class="tw:label tw:pl-4 tw:flex tw:items-center">

                <span class="tw:select-none tw:cursor-pointer">Include in emailed notification summary</span>

                <input type="checkbox" id="renewalEmail" :disabled="!generalPrefs.notificationsEnabled || !renewalPrefs.enabled || !generalPrefs.sendEmail" @change="handlePreferenceUpdate"
                       v-model="renewalPrefs.sendEmail" :checked="renewalPrefs.sendEmail"
                       class="tw:toggle tw:toggle-md tw:toggle-success tw:border tw:border-solid tw:border-gray-400"
                />
              </label>

              <div class="tw:pl-4 tw:pt-2">Receive this notification when I have:</div>

              <div class="tw:flex tw:pl-4">
                <label for="renewalServicingRole" class="tw:label tw:flex tw:items-center tw:gap-2">

                  <span class="tw:pl-4 tw:select-none tw:cursor-pointer">Servicing Role</span>
                  <input type="checkbox" id="renewalServicingRole" :disabled="!generalPrefs.notificationsEnabled || !renewalPrefs.enabled" @change="handlePreferenceUpdate"
                         v-model="renewalPrefs.servicingRole" :checked="renewalPrefs.servicingRole"
                         class="tw:checkbox tw:checkbox-success tw:checkbox-sm tw:border tw:border-solid tw:border-gray-400"
                  />
                </label>

                <label for="renewalFavorited" class="tw:label tw:flex tw:items-center tw:gap-2">
                  <span class="tw:pl-2 tw:select-none tw:cursor-pointer">Favorited</span>
                  <input type="checkbox" id="renewalFavorited" :disabled="!generalPrefs.notificationsEnabled || !renewalPrefs.enabled" @change="handlePreferenceUpdate"
                         v-model="renewalPrefs.favorited" :checked="renewalPrefs.favorited"
                         class="tw:checkbox tw:checkbox-success tw:checkbox-sm tw:border tw:border-solid tw:border-gray-400"
                  />
                </label>
              </div>

              <div class="tw:pl-4 tw:pt-2">Receive this notification this many days before expiration:</div>

              <div class="tw:flex tw:pl-4">
                <label for="renewalDaysBefore30" class="tw:label tw:flex tw:justify-end tw:gap-2">
                  <span class="tw:pl-4 tw:select-none tw:cursor-pointer">30 days</span>
                  <input type="checkbox" id="renewalDaysBefore30" :disabled="!generalPrefs.notificationsEnabled || !renewalPrefs.enabled" @change="handlePreferenceUpdate"
                         v-model="renewalPrefs.daysBefore30" :checked="renewalPrefs.daysBefore30"
                         class="tw:checkbox tw:checkbox-success tw:checkbox-sm tw:border tw:border-solid tw:border-gray-400"
                  />
                </label>

                <label for="renewalDaysBefore60" class="tw:label tw:flex tw:justify-end tw:gap-2">
                  <span class="tw:pl-2 tw:select-none tw:cursor-pointer">60 days</span>
                  <input type="checkbox" id="renewalDaysBefore60" :disabled="!generalPrefs.notificationsEnabled || !renewalPrefs.enabled" @change="handlePreferenceUpdate"
                         v-model="renewalPrefs.daysBefore60" :checked="renewalPrefs.daysBefore60"
                         class="tw:checkbox tw:checkbox-success tw:checkbox-sm tw:border tw:border-solid tw:border-gray-400"
                  />
                </label>

                <label for="renewalDaysBefore90" class="tw:label tw:flex tw:justify-end tw:gap-2">
                  <span class="tw:pl-2 tw:select-none tw:cursor-pointer">90 days</span>
                  <input type="checkbox" id="renewalDaysBefore90" :disabled="!generalPrefs.notificationsEnabled || !renewalPrefs.enabled" @change="handlePreferenceUpdate"
                         v-model="renewalPrefs.daysBefore90" :checked="renewalPrefs.daysBefore90"
                         class="tw:checkbox tw:checkbox-success tw:checkbox-sm tw:border tw:border-solid tw:border-gray-400"
                  />
                </label>

                <label for="renewalDaysBefore120" class="tw:label tw:flex tw:justify-end tw:gap-2">
                  <span class="tw:pl-2 tw:select-none tw:cursor-pointer">120 days</span>
                  <input type="checkbox" id="renewalDaysBefore120" :disabled="!generalPrefs.notificationsEnabled || !renewalPrefs.enabled" @change="handlePreferenceUpdate"
                         v-model="renewalPrefs.daysBefore120" :checked="renewalPrefs.daysBefore120"
                         class="tw:checkbox tw:checkbox-success tw:checkbox-sm tw:border tw:border-solid tw:border-gray-400"
                  />
                </label>
              </div>
            </div>
          </article>
        </section>
        <!-- New Policies in Epic -->
        <section v-if="!isClientUser" class="tw:p-5 tw:flex tw:flex-col tw:rounded-xl tw:border tw:border-solid tw:border-gray-200 tw:drop-shadow-xs">
          <h2 class="tw:text-lg tw:font-bold">New Policies Entered Into Epic</h2>

          <article>
            <div class="tw:mt-2 tw:flex tw:flex-col tw:w-full tw:gap-0.5">

              <label for="newPolicyEnabled" class="tw:label tw:pl-4 tw:flex tw:items-center">
                <span class="tw:select-none tw:cursor-pointer">Enable notifications</span>
                <input type="checkbox" id="newPolicyEnabled" :disabled="!generalPrefs.notificationsEnabled" @change="handlePreferenceUpdate"
                       v-model="newPolicyPrefs.enabled" :checked="newPolicyPrefs.enabled"
                       class="tw:toggle tw:toggle-md tw:toggle-success tw:border tw:border-solid tw:border-gray-400"
                />
              </label>

              <label for="newPolicySendEmail" class="tw:label tw:pl-4 tw:flex tw:items-center">
                <span class="tw:select-none tw:cursor-pointer">Include in emailed notification summary</span>
                <input type="checkbox" id="newPolicySendEmail" :disabled="!generalPrefs.notificationsEnabled || !newPolicyPrefs.enabled || !generalPrefs.sendEmail" @change="handlePreferenceUpdate"
                       v-model="newPolicyPrefs.sendEmail" :checked="newPolicyPrefs.sendEmail"
                       class="tw:toggle tw:toggle-md tw:toggle-success tw:border tw:border-solid tw:border-gray-400"
                />
              </label>

              <div class="tw:pl-4 tw:pt-2">Receive this notification when I have:</div>

              <div class="tw:flex tw:pl-4">

                <label for="newPolicyServicingRole" class="tw:label tw:flex tw:items-center tw:gap-2">
                  <span class="tw:pl-4 tw:select-none tw:cursor-pointer">Servicing Role</span>
                  <input type="checkbox" id="newPolicyServicingRole" :disabled="!generalPrefs.notificationsEnabled || !newPolicyPrefs.enabled" @change="handlePreferenceUpdate"
                         v-model="newPolicyPrefs.servicingRole" :checked="newPolicyPrefs.servicingRole"
                         class="tw:checkbox tw:checkbox-success tw:checkbox-sm tw:border tw:border-solid tw:border-gray-400"
                  />
                </label>
                <label for="newPolicyFavorited" class="tw:label tw:flex tw:items-center tw:gap-2">
                  <span class="tw:pl-2 tw:select-none tw:cursor-pointer">Favorited</span>
                  <input type="checkbox" id="newPolicyFavorited" :disabled="!generalPrefs.notificationsEnabled || !newPolicyPrefs.enabled" @change="handlePreferenceUpdate"
                         v-model="newPolicyPrefs.favorited" :checked="newPolicyPrefs.favorited"
                         class="tw:checkbox tw:checkbox-success tw:checkbox-sm tw:border tw:border-solid tw:border-gray-400"
                  />
                </label>
              </div>
            </div>
          </article>
        </section>
        <!-- New Account/Policy Uploaded Documents -->
        <section v-if="!isClientUser" class="tw:p-5 tw:flex tw:flex-col tw:rounded-xl tw:border tw:border-solid tw:border-gray-200 tw:drop-shadow-xs">
          <h2 class="tw:text-lg tw:font-bold">New Account/Policy Uploaded Documents</h2>

          <article>
            <div class="tw:mt-2 tw:flex tw:flex-col tw:w-full tw:gap-0.5">

              <label for="newUploadDocumentEnabled" class="tw:label tw:pl-4 tw:flex tw:items-center">
                <span class="tw:select-none tw:cursor-pointer">Enable notifications</span>
                <input type="checkbox" id="newUploadDocumentEnabled" :disabled="!generalPrefs.notificationsEnabled" @change="handlePreferenceUpdate"
                       v-model="newDocumentUploadPrefs.enabled" :checked="newDocumentUploadPrefs.enabled"
                       class="tw:toggle tw:toggle-md tw:toggle-success tw:border tw:border-solid tw:border-gray-400"
                />
              </label>

              <label for="newUploadDocumentSendEmail" class="tw:label tw:pl-4 tw:flex tw:items-center">
                <span class="tw:select-none tw:cursor-pointer">Include in emailed notification summary</span>
                <input type="checkbox" id="newUploadDocumentSendEmail" :disabled="!generalPrefs.notificationsEnabled || !newDocumentUploadPrefs.enabled || !generalPrefs.sendEmail" @change="handlePreferenceUpdate"
                       v-model="newDocumentUploadPrefs.sendEmail" :checked="newDocumentUploadPrefs.sendEmail"
                       class="tw:toggle tw:toggle-md tw:toggle-success tw:border tw:border-solid tw:border-gray-400"
                />
              </label>

              <div class="tw:pl-4 tw:pt-2">Receive this notification when I have:</div>

              <div class="tw:flex tw:pl-4">

                <label for="newUploadDocumentServicingRole" class="tw:label tw:flex tw:items-center tw:gap-2">
                  <span class="tw:pl-4 tw:select-none tw:cursor-pointer">Servicing Role</span>
                  <input type="checkbox" id="newUploadDocumentServicingRole" :disabled="!generalPrefs.notificationsEnabled || !newDocumentUploadPrefs.enabled" @change="handlePreferenceUpdate"
                         v-model="newDocumentUploadPrefs.servicingRole" :checked="newDocumentUploadPrefs.servicingRole"
                         class="tw:checkbox tw:checkbox-success tw:checkbox-sm tw:border tw:border-solid tw:border-gray-400"
                  />
                </label>
                <label for="newUploadDocumentFavorited" class="tw:label tw:flex tw:items-center tw:gap-2">
                  <span class="tw:pl-2 tw:select-none tw:cursor-pointer">Favorited</span>
                  <input type="checkbox" id="newUploadDocumentFavorited" :disabled="!generalPrefs.notificationsEnabled || !newDocumentUploadPrefs.enabled" @change="handlePreferenceUpdate"
                         v-model="newDocumentUploadPrefs.favorited" :checked="newDocumentUploadPrefs.favorited"
                         class="tw:checkbox tw:checkbox-success tw:checkbox-sm tw:border tw:border-solid tw:border-gray-400"
                  />
                </label>
              </div>
            </div>
          </article>
        </section>
        <!-- New Client Uploaded Documents -->
        <section v-if="!isClientUser" class="tw:p-5 tw:flex tw:flex-col tw:rounded-xl tw:border tw:border-solid tw:border-gray-200 tw:drop-shadow-xs">
          <h2 class="tw:text-lg tw:font-bold">New Client Uploaded Documents</h2>

          <article>
            <div class="tw:mt-2 tw:flex tw:flex-col tw:w-full tw:gap-0.5">

              <label for="newClientDocumentUploadEnabled" class="tw:label tw:pl-4 tw:flex tw:items-center">
                <span class="tw:select-none tw:cursor-pointer">Enable notifications</span>
                <input type="checkbox" id="newClientDocumentUploadEnabled" :disabled="!generalPrefs.notificationsEnabled" @change="handlePreferenceUpdate"
                       v-model="newClientDocumentUploadPrefs.enabled" :checked="newClientDocumentUploadPrefs.enabled"
                       class="tw:toggle tw:toggle-md tw:toggle-success tw:border tw:border-solid tw:border-gray-400"
                />
              </label>

              <label for="newClientDocumentUploadSendEmail" class="tw:label tw:pl-4 tw:flex tw:items-center">
                <span class="tw:select-none tw:cursor-pointer">Include in emailed notification summary</span>
                <input type="checkbox" id="newClientDocumentUploadSendEmail" :disabled="!generalPrefs.notificationsEnabled || !newClientDocumentUploadPrefs.enabled || !generalPrefs.sendEmail" @change="handlePreferenceUpdate"
                       v-model="newClientDocumentUploadPrefs.sendEmail" :checked="newClientDocumentUploadPrefs.sendEmail"
                       class="tw:toggle tw:toggle-md tw:toggle-success tw:border tw:border-solid tw:border-gray-400"
                />
              </label>

              <div class="tw:pl-4 tw:pt-2">Receive this notification when I have:</div>

              <div class="tw:flex tw:pl-4">

                <label for="newClientDocumentUploadServicingRole" class="tw:label tw:flex tw:items-center tw:gap-2">
                  <span class="tw:pl-4 tw:select-none tw:cursor-pointer">Servicing Role</span>
                  <input type="checkbox" id="newClientDocumentUploadServicingRole" :disabled="!generalPrefs.notificationsEnabled || !newClientDocumentUploadPrefs.enabled" @change="handlePreferenceUpdate"
                         v-model="newClientDocumentUploadPrefs.servicingRole" :checked="newClientDocumentUploadPrefs.servicingRole"
                         class="tw:checkbox tw:checkbox-success tw:checkbox-sm tw:border tw:border-solid tw:border-gray-400"
                  />
                </label>
                <label for="newClientDocumentUploadFavorited" class="tw:label tw:flex tw:items-center tw:gap-2">
                  <span class="tw:pl-2 tw:select-none tw:cursor-pointer">Favorited</span>
                  <input type="checkbox" id="newClientDocumentUploadFavorited" :disabled="!generalPrefs.notificationsEnabled || !newClientDocumentUploadPrefs.enabled" @change="handlePreferenceUpdate"
                         v-model="newClientDocumentUploadPrefs.favorited" :checked="newClientDocumentUploadPrefs.favorited"
                         class="tw:checkbox tw:checkbox-success tw:checkbox-sm tw:border tw:border-solid tw:border-gray-400"
                  />
                </label>
              </div>
            </div>
          </article>
        </section>
        <!-- New Analytics Resource Published -->
        <section v-if="isClientUser" class="tw:p-5 tw:flex tw:flex-col tw:rounded-xl tw:border tw:border-solid tw:border-gray-200 tw:drop-shadow-xs">
          <h2 class="tw:text-lg tw:font-bold">New Analytics Resource Available</h2>

          <article>
            <div class="tw:mt-2 tw:flex tw:flex-col tw:w-full tw:gap-0.5">

              <label for="newAnalyticsResourceEnabled" class="tw:label tw:pl-4 tw:flex tw:items-center">
                <span class="tw:select-none tw:cursor-pointer">Enable notifications</span>
                <input type="checkbox" id="newAnalyticsResourceEnabled" :disabled="!generalPrefs.notificationsEnabled" @change="handlePreferenceUpdate"
                       v-model="newAnalyticsResourcePrefs.enabled" :checked="newAnalyticsResourcePrefs.enabled"
                       class="tw:toggle tw:toggle-md tw:toggle-success tw:border tw:border-solid tw:border-gray-400"
                />
              </label>

              <label for="newAnalyticsResourceSendEmail" class="tw:label tw:pl-4 tw:flex tw:items-center">
                <span class="tw:select-none tw:cursor-pointer">Include in emailed notification summary</span>
                <input type="checkbox" id="newAnalyticsResourceSendEmail" :disabled="!generalPrefs.notificationsEnabled || !newAnalyticsResourcePrefs.enabled || !generalPrefs.sendEmail" @change="handlePreferenceUpdate"
                       v-model="newAnalyticsResourcePrefs.sendEmail" :checked="newAnalyticsResourcePrefs.sendEmail"
                       class="tw:toggle tw:toggle-md tw:toggle-success tw:border tw:border-solid tw:border-gray-400"
                />
              </label>
            </div>
          </article>
        </section>
      </div>
    </main>
  </div>
</template>
